import React, { createContext, useState, useContext, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext'; // Import useAuth for currentUser
import { usePolicy } from '../contexts/PolicyContext';
import { api } from '../components/role-play/apiService';
import { VoiceModulationWire } from '../utils/voice-modulation/types';

// Define types for the role play selections
export type RolePlaySelections = {
  base_config_id: string;
  student_profile_id: string; // Legacy field name
  character_profile_id?: string; // New field name (CloudSQL migration)
  scenarios_id: string;
  corrective_teaching_step_id: string;
  preferred_name?: string;
  notes?: string;
  // Prompt mode fields
  prompt_id?: string;
  prompt_name?: string;
  generated_prompt?: string;
  prompt_session_duration?: number;
  intro_prompt?: string; // text for pre-session intro audio
};

// Define component details type (matches your RolePlayComponentDetails)
export type RolePlayComponentDetail = {
  id: string;
  name: string;
  description: string;
  age?: number;
  background?: string;
  ai_voice?: string; // Add this line for avatar selection compatibility
  gender?: string;
  avatar_type?: string; // Added for avatar mapping
  avatar_asset_id?: string;
  avatar_signed_url?: string;
  // Per-character voice modulation resolved for the caller's org (computed server-side).
  voice_modulation?: VoiceModulationWire | null;
  // Behavior category fields
  module?: string;
  scenarioDescription?: string;
  skills?: string[];
  examples?: string[];
  student_background?: string;
  // Corrective steps fields
  steps?: {
    [key: string]: {
      title: string;
      description: string;
      student_response: string;
    };
  };
  // Add session_duration for teaching method
  session_duration?: number;
};

export type RolePlayComponentDetails = {
  base_config: RolePlayComponentDetail | null;
  student_profile: RolePlayComponentDetail | null;
  scenarios: RolePlayComponentDetail | null;
  module: RolePlayComponentDetail | null;
};

// Add type for RolePlay (from RolePlaySelector)
type RolePlay = {
  id: string;
  name: string;
  description: string;
  // Optionally, other fields
  studentName?: string;
  studentAge?: number;
  background?: string;
  gender?: string;
  module?: string;
  scenarioDescription?: string;
  skills?: string[];
  examples?: string[];
  steps?: any;
};

type AvailableOptions = Record<'base_config' | 'student_profile' | 'scenarios' | 'module', RolePlay[]>;

// Context type definition
type RolePlayContextType = {
  rolePlaySelections: RolePlaySelections;
  componentDetails: RolePlayComponentDetails;
  setRolePlaySelections: (selections: Record<string, string | number | undefined>) => void;
  setComponentDetails: (details: RolePlayComponentDetails) => void;
  hasCompleteSelections: boolean;
  availableOptions: AvailableOptions;
  loading: boolean;
  error: string | null;
  fetchAvailableOptions: () => Promise<void>;
  refreshOptions: () => Promise<void>;
  lastRefreshed: Date | null;
};

// Create the context with a default value
const RolePlayContext = createContext<RolePlayContextType | undefined>(undefined);

// Local storage constants
const STORAGE_KEY_PREFIX = 'rolePlay_';
const STORAGE_KEYS = {
  OPTIONS: `${STORAGE_KEY_PREFIX}Options`,
  LAST_REFRESHED: `${STORAGE_KEY_PREFIX}LastRefreshed`,
  CACHE_EXPIRY_HOURS: 24 // Cache validity in hours
};

// Helper function to safely interact with localStorage
const safeStorage = {
  get: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  },
  set: (key: string, value: string): boolean => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Error writing to localStorage:', error);
      return false;
    }
  },
  remove: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  }
};

// Provider props type
type RolePlayProviderProps = {
  children: ReactNode;
};

// Provider component
export const RolePlayProvider: React.FC<RolePlayProviderProps> = ({ children }) => {
  // Initialize state with empty values
  const [rolePlaySelections, setSelections] = useState<RolePlaySelections>({
    base_config_id: '',
    student_profile_id: '',
    scenarios_id: '',
    corrective_teaching_step_id: '',
    preferred_name: '',
    notes: '',
    prompt_id: '',
    prompt_name: '',
    generated_prompt: '',
  });

  const [componentDetails, setDetails] = useState<RolePlayComponentDetails>({
    base_config: null,
    student_profile: null,
    scenarios: null,
    module: null
  });

  // Initialize with cached data and check expiry
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(() => {
    const lastRefreshedStr = safeStorage.get(STORAGE_KEYS.LAST_REFRESHED);
    return lastRefreshedStr ? new Date(lastRefreshedStr) : null;
  });

  const [availableOptions, setAvailableOptions] = useState<AvailableOptions>(() => {
    const cached = safeStorage.get(STORAGE_KEYS.OPTIONS);
    if (cached && lastRefreshed) {
      try {
        const parsed = JSON.parse(cached);
        // Map backend 'corrective_steps' to frontend 'module'
        return {
          ...parsed,
          module: parsed.corrective_steps || [],
        };
      } catch (error) {
        console.error('Error parsing cached role play options:', error);
      }
    }
    return {
      base_config: [],
      student_profile: [],
      scenarios: [],
      module: []
    };
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Get currentUser from AuthContext
  const { currentUser, loading: authLoading } = useAuth();
  const { policiesAccepted } = usePolicy();

  // Function to update selections
  const setRolePlaySelections = (selections: Record<string, string | number | undefined>) => {
    setSelections({
      base_config_id: String(selections.base_config || ''),
      student_profile_id: String(selections.student_profile || ''),
      scenarios_id: String(selections.scenarios || ''),
      corrective_teaching_step_id: String(selections.module || ''),
      preferred_name: String(selections.preferred_name || ''),
      notes: String(selections.notes || ''),
      prompt_id: String(selections.prompt_id || ''),
      prompt_name: String(selections.prompt_name || ''),
      generated_prompt: String(selections.generated_prompt || ''),
      prompt_session_duration: selections.prompt_session_duration ? Number(selections.prompt_session_duration) : undefined,
      intro_prompt: String(selections.intro_prompt || ''),
    });
  };

  // Function to update component details
  const setComponentDetails = (details: RolePlayComponentDetails) => {
    // If module exists and has session_duration, ensure it is set
    let updatedDetails = { ...details };
    if (details.module && (details.module as any).session_duration !== undefined) {
      updatedDetails.module = {
        ...details.module,
        session_duration: (details.module as any).session_duration
      };
    }
    setDetails(updatedDetails);
  };

  // Function to explicitly refresh the data
  const refreshOptions = async () => {
    return fetchAvailableOptions(true);
  };

  // Fetch all options from backend
  const fetchAvailableOptions = async (forceRefresh = false) => {
    // Check if cache is still valid and we're not forcing a refresh
    if (!forceRefresh && lastRefreshed) {
      const now = new Date();
      const expiryTime = new Date(lastRefreshed.getTime() + STORAGE_KEYS.CACHE_EXPIRY_HOURS * 60 * 60 * 1000);
      if (now < expiryTime && availableOptions.base_config.length > 0) {
        // Data is still valid, no need to refetch
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      if (!currentUser) {
        setError('Please log in to access role play configurations');
        setLoading(false);
        return;
      }
      const endpoints = {
        base_config: '/base-config',
        student_profile: '/student-profile',
        scenarios: '/scenario',
        corrective_steps: '/teaching-method' // backend key
      };
      const results: any = {
        base_config: [],
        student_profile: [],
        scenarios: [],
        corrective_steps: []
      };
      await Promise.all(
        Object.entries(endpoints).map(async ([category, endpoint]) => {
          try {
            const data = await api.get(endpoint);
            results[category] = data;
          } catch (err) {
            results[category] = [];
          }
        })
      );
      // Map backend 'corrective_steps' to frontend 'module'
      setAvailableOptions({
        base_config: results.base_config,
        student_profile: results.student_profile,
        scenarios: results.scenarios,
        module: results.corrective_steps
      });

      // Update last refreshed timestamp
      const now = new Date();
      setLastRefreshed(now);

      // Save to localStorage with the updated timestamp
      safeStorage.set(STORAGE_KEYS.OPTIONS, JSON.stringify(results));
      safeStorage.set(STORAGE_KEYS.LAST_REFRESHED, now.toISOString());
    } catch (err) {
      setError('Failed to load role play options. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount if not already loaded or if data is expired.
  // Gated on policiesAccepted so we don't fire role-play config calls while the
  // policy-acceptance modal is blocking the app.
  React.useEffect(() => {
    if (!authLoading && currentUser && policiesAccepted === true) {
      const allEmpty = Object.values(availableOptions).every(arr => arr.length === 0);
      const isExpired = lastRefreshed
        ? new Date() > new Date(lastRefreshed.getTime() + STORAGE_KEYS.CACHE_EXPIRY_HOURS * 60 * 60 * 1000)
        : true;

      if (allEmpty || isExpired) {
        fetchAvailableOptions();
      }
    }
  }, [currentUser, authLoading, policiesAccepted]);

  // Check if all selections are complete
  const hasCompleteSelections = Object.values(rolePlaySelections).every(Boolean);

  // Create the context value
  const contextValue: RolePlayContextType = {
    rolePlaySelections,
    componentDetails,
    setRolePlaySelections,
    setComponentDetails,
    hasCompleteSelections,
    availableOptions,
    loading,
    error,
    fetchAvailableOptions,
    refreshOptions,
    lastRefreshed
  };

  return (
    <RolePlayContext.Provider value={contextValue}>
      {children}
    </RolePlayContext.Provider>
  );
};

// Custom hook to use the context
export const useRolePlay = () => {
  const context = useContext(RolePlayContext);
  if (context === undefined) {
    throw new Error('useRolePlay must be used within a RolePlayProvider');
  }
  return context;
};